/**
 * File: contractSchedule.ts
 * Path: packages/config/src/contracts/contractSchedule.ts
 * Description: What the client pays, when, and the deposit: the first
 * payment of the plan they chose.
 *
 * Deposit, per Liam (1 Oct 2026): all of it for Pay Upfront, half for The
 * Fair Split, the first stage for Pay As We Build, the first monthly
 * instalment for the 6- and 12-month plans, the first month for retainers
 * (and the venture retainer), and the blueprint fee for stage-built work, which is
 * the first thing a FORGE client pays.
 *
 * Instalment maths is the quote engine's (computePricing): total = fee plus
 * the plan's mark-up, each instalment is the rounded share, and the last one
 * takes the rounding so the lines add up to the total exactly.
 */

import { monthlyDueDayPhrase, monthlyDueDayRow } from "./contractDueDay";
import { PAYMENT_PLANS } from "../pricing/paymentConstants";
import type { PaymentPlanId } from "../pricing/pricingTypes";
import {
  DEPOSIT_PART,
  formatPaymentReference,
  monthPart,
  phasePart,
  SETTLEMENT_PART,
  sprintPart,
} from "../references/references";
import { formatRand } from "./contractFormat";
import type {
  ContractContent,
  ContractPayment,
  ContractPaymentReference,
  ContractSchedule,
  ContractScheduleLine,
} from "./contractTypes";

/**
 * Plan names as the contract prints them, from the published plans. "Pay
 * Upfront" carries no saving: Liam dropped the old template's "Pay Upfront &
 * Save" 10% discount.
 */
export const CONTRACT_PLAN_LABELS: Record<PaymentPlanId, string> = {
  once_off: PAYMENT_PLANS.once_off.label,
  split_50_50: PAYMENT_PLANS.split_50_50.label,
  phase_by_phase: PAYMENT_PLANS.phase_by_phase.label,
  split_6_month: PAYMENT_PLANS.split_6_month.label,
  split_12_month: PAYMENT_PLANS.split_12_month.label,
};

const money = (n: number): number =>
  Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;

const instalments = (
  total: number,
  count: number,
  label: string,
): ContractScheduleLine[] => {
  const each = Math.round(total / count);
  return Array.from({ length: count }, (_, i) => ({
    label: `${label} ${i + 1} of ${count}`,
    amount: i === count - 1 ? money(total - each * (count - 1)) : each,
    due: i === 0 ? "Before work starts" : `Month ${i + 1}`,
  }));
};

const serviceSchedule = (c: ContractContent): ContractSchedule => {
  const fee = money(c.fee);
  const planLabel =
    CONTRACT_PLAN_LABELS[c.paymentPlan] ?? CONTRACT_PLAN_LABELS.once_off;

  switch (c.paymentPlan) {
    case "split_50_50": {
      const first = Math.round(fee / 2);
      return {
        planLabel,
        total: fee,
        totalLabel: "Total fee",
        lines: [
          { label: "First 50%", amount: first, due: "Before work starts" },
          {
            label: "Second 50%",
            amount: money(fee - first),
            due: "On delivery and Client approval",
          },
        ],
        deposit: { amount: first, label: "50% before work starts" },
      };
    }
    case "phase_by_phase": {
      const stages =
        c.stages.length > 0 ? c.stages : [{ label: "Project", amount: fee }];
      const lines = stages.map((s, i) => ({
        label: s.label || `Stage ${i + 1}`,
        amount: money(s.amount),
        due:
          i === 0
            ? "Before work starts"
            : "On completion of the previous stage",
      }));
      return {
        planLabel,
        total: money(lines.reduce((sum, l) => sum + l.amount, 0)),
        totalLabel: "Total fee",
        lines,
        deposit: {
          amount: lines[0].amount,
          label: `First stage: ${lines[0].label}`,
        },
      };
    }
    case "split_6_month":
    case "split_12_month": {
      const meta = PAYMENT_PLANS[c.paymentPlan];
      const count = meta.instalments ?? 1;
      const total = Math.round(fee * (1 + meta.markup));
      const lines = instalments(total, count, "Instalment");
      return {
        planLabel,
        total,
        totalLabel: `Total fee, fixed for the ${meta.instalments}-month plan`,
        lines,
        deposit: { amount: lines[0].amount, label: "First monthly instalment" },
      };
    }
    case "once_off":
    default:
      return {
        planLabel: CONTRACT_PLAN_LABELS.once_off,
        total: fee,
        totalLabel: "Total fee",
        lines: [
          { label: "Full payment", amount: fee, due: "Before work starts" },
        ],
        deposit: { amount: fee, label: "Full payment before work starts" },
      };
  }
};

const retainerSchedule = (
  c: ContractContent,
  label: string,
): ContractSchedule => {
  const monthly = money(c.monthlyFee);
  const months = Math.max(1, Math.round(c.minimumTermMonths));
  const total = money(monthly * months);

  if (c.template === "retainer" && c.retainerBilling === "once_off") {
    return {
      planLabel: CONTRACT_PLAN_LABELS.once_off,
      total,
      totalLabel: "Total minimum contract value",
      lines: [
        {
          label: `${months} months paid in advance`,
          amount: total,
          due: "Before work starts",
        },
      ],
      deposit: { amount: total, label: `${months} months paid in advance` },
    };
  }

  return {
    planLabel: label,
    total,
    totalLabel:
      c.template === "venture"
        ? "Minimum partnership retainer value"
        : "Total minimum contract value",
    lines: [
      { label: "First month", amount: monthly, due: "Before work starts" },
      {
        label: `Months 2 to ${months}`,
        amount: money(monthly * (months - 1)),
        due: `${formatRand(monthly)} ${monthlyDueDayPhrase(c.monthlyDueDay).replace("calendar ", "")}`,
      },
    ].filter((l) => l.amount > 0),
    deposit: { amount: monthly, label: "First month's retainer" },
  };
};

const sprintSchedule = (c: ContractContent): ContractSchedule => {
  const blueprint = money(c.blueprintFee);
  const sprint = money(c.sprintFee);
  const sprints = Math.max(1, Math.round(c.estimatedSprints));
  const credit = c.blueprintCredit ? blueprint : 0;
  const lines: ContractScheduleLine[] = [
    {
      label: "Blueprint fee",
      amount: blueprint,
      due: "Before any development starts",
    },
    {
      label: `Build stages (estimated ${sprints})`,
      amount: money(sprint * sprints),
      due: `${formatRand(sprint)} at the end of each stage`,
    },
  ];
  if (credit > 0) {
    lines.push({
      label: "Blueprint credit",
      amount: -credit,
      due: "Taken off the stage invoices",
    });
  }
  return {
    planLabel: "Billing per stage",
    total: money(lines.reduce((sum, l) => sum + l.amount, 0)),
    totalLabel:
      "Estimated total (the Blueprint fixes the price and the number of stages)",
    lines,
    deposit: { amount: blueprint, label: "Blueprint fee" },
  };
};

export const computeContractSchedule = (
  content: ContractContent,
): ContractSchedule => {
  switch (content.template) {
    case "retainer":
      return retainerSchedule(content, "Monthly retainer");
    case "venture":
      return retainerSchedule(content, "Monthly partnership retainer");
    case "sprint":
      return sprintSchedule(content);
    case "service":
    default:
      return serviceSchedule(content);
  }
};

/** Equal Pay As We Build stages that add up to the fee exactly. */
export const splitIntoStages = (
  fee: number,
  labels: readonly string[] = DEFAULT_STAGE_LABELS,
): Array<{ label: string; amount: number }> => {
  const count = Math.max(1, labels.length);
  const each = Math.round(fee / count);
  return labels.map((label, i) => ({
    label,
    amount: i === count - 1 ? money(fee - each * (count - 1)) : each,
  }));
};

export const DEFAULT_STAGE_LABELS = [
  "Kick-off and design sign-off",
  "Build ready for review",
  "Launch and handover",
] as const;

// ─── Each payment, with what it is for ───────────────────────────────────

const servicePayments = (c: ContractContent): ContractPayment[] => {
  const lines = serviceSchedule(c).lines;
  switch (c.paymentPlan) {
    case "split_50_50":
      return [
        { ...lines[0], part: DEPOSIT_PART, label: "Deposit (first 50%)" },
        {
          ...lines[1],
          part: SETTLEMENT_PART,
          label: "Settlement (second 50%)",
        },
      ];
    case "phase_by_phase":
      return lines.map((l, i) => ({
        ...l,
        part: phasePart(i + 1),
        label: `Phase ${i + 1}: ${l.label}`,
      }));
    case "split_6_month":
    case "split_12_month":
      return lines.map((l, i) => ({
        ...l,
        part: monthPart(i + 1),
        label: `Instalment ${i + 1} of ${lines.length}`,
      }));
    case "once_off":
    default:
      return [{ ...lines[0], part: DEPOSIT_PART, label: "Full payment" }];
  }
};

const retainerPayments = (c: ContractContent): ContractPayment[] => {
  const months = Math.max(1, Math.round(c.minimumTermMonths));
  const fee = money(c.monthlyFee);
  if (c.template === "retainer" && c.retainerBilling === "once_off") {
    return [
      {
        part: DEPOSIT_PART,
        label: `Full payment (${months} months in advance)`,
        amount: money(fee * months),
        due: "Before work starts",
      },
    ];
  }
  return Array.from({ length: months }, (_, i) => ({
    part: monthPart(i + 1),
    label: `Month ${i + 1}`,
    amount: fee,
    due:
      i === 0 ? "Before work starts" : monthlyDueDayRow(c.monthlyDueDay, i + 1),
  }));
};

/** The blueprint credit comes off the first stages until it is used up. */
const sprintPayments = (c: ContractContent): ContractPayment[] => {
  const sprint = money(c.sprintFee);
  const sprints = Math.max(1, Math.round(c.estimatedSprints));
  let credit = c.blueprintCredit ? money(c.blueprintFee) : 0;
  const payments: ContractPayment[] = [
    {
      part: DEPOSIT_PART,
      label: "Blueprint fee",
      amount: money(c.blueprintFee),
      due: "Before any development starts",
    },
  ];
  for (let i = 1; i <= sprints; i += 1) {
    const off = Math.min(credit, sprint);
    credit = money(credit - off);
    const amount = money(sprint - off);
    if (amount <= 0) continue;
    payments.push({
      part: sprintPart(i),
      label: off > 0 ? `Stage ${i} (less the blueprint credit)` : `Stage ${i}`,
      amount,
      due: `At the end of stage ${i}`,
    });
  }
  return payments;
};

/**
 * Every payment the plan schedules, first (the deposit) to last, each with
 * what it is for. They add up to the schedule's total.
 */
export const contractPayments = (
  content: ContractContent,
): ContractPayment[] => {
  switch (content.template) {
    case "retainer":
    case "venture":
      return retainerPayments(content);
    case "sprint":
      return sprintPayments(content);
    case "service":
    default:
      return servicePayments(content);
  }
};

/**
 * The payments with the reference to use for each. Empty while the contract
 * has no reference (a draft without a numbered client).
 */
export const contractPaymentReferences = (
  reference: string,
  content: ContractContent,
): ContractPaymentReference[] =>
  reference.trim()
    ? contractPayments(content).map((p) => ({
        ...p,
        reference: formatPaymentReference(reference, p.part),
      }))
    : [];

/** "Deposit (first 50%): IDK-C012-02-DEP, R9,250.00". */
export const paymentReferenceLine = (p: ContractPaymentReference): string =>
  `${p.label}: ${p.reference}, ${formatRand(p.amount)}`;
